import { type ReactNode } from 'react';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { ChipCircleDeleteIcon } from './ChipCircleDeleteIcon';

const defaultBackgroundColor = '#F0F5FF';
const defaultHeightPx = 24;

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    borderRadius: `${defaultHeightPx / 2}px`,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: '4px',
    marginLeft: '4px',
    marginRight: '4px',
    maxWidth: '100%',
    minWidth: 0,
  },
  startAdornment: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: '6px',
  },
  label: {
    fontSize: '12px',
    lineHeight: '18px',
    color: '#3B3C40',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    paddingRight: '4px',
  },
  removeButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginTop: 0,
    marginRight: '6px',
    marginBottom: 0,
    marginLeft: '4px',
    width: '14px',
    height: '14px',
    flexShrink: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    '&:focus-visible': {
      outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '1px',
      borderRadius: '4px',
    },
  },
};

export interface RemovableChipProps {
  label: string;
  onRemove: () => void;
  removeAriaLabel: string;
  /** Optional leading content (e.g. icon). */
  startAdornment?: ReactNode;
  backgroundColor?: string;
  heightPx?: number;
  sx?: SxProps<Theme>;
  /** Defaults to the circular primary close icon. */
  removeIcon?: ReactNode;
}

function getRemovableChipRootSx(
  backgroundColor: string,
  heightPx: number,
  hasStartAdornment: boolean,
  sx: RemovableChipProps['sx'],
): SxProps<Theme> {
  const height = `${heightPx}px`;
  return [
    customStyles.root,
    {
      backgroundColor,
      height,
      minHeight: height,
      maxHeight: height,
      paddingLeft: hasStartAdornment ? '6px' : '10px',
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];
}

/**
 * Chip-style label with an explicit remove control (not MUI Chip), so the delete action
 * is not swallowed by Chip’s internal ButtonBase / event handling.
 * Parents can detect remove clicks via `[data-removable-chip-remove="true"]`.
 */
export function RemovableChip({
  label,
  onRemove,
  removeAriaLabel,
  startAdornment,
  backgroundColor = defaultBackgroundColor,
  heightPx = defaultHeightPx,
  sx,
  removeIcon,
}: RemovableChipProps) {
  const rootSx = getRemovableChipRootSx(backgroundColor, heightPx, Boolean(startAdornment), sx);

  const resolvedRemoveIcon = removeIcon ?? <ChipCircleDeleteIcon />;

  const handleRemoveClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove();
  };

  return (
    <Box sx={rootSx}>
      {startAdornment ? (
        <Box
          component='span'
          sx={customStyles.startAdornment}
        >
          {startAdornment}
        </Box>
      ) : null}
      <Typography
        component='span'
        sx={customStyles.label}
      >
        {label}
      </Typography>
      <Box
        component='button'
        type='button'
        data-removable-chip-remove='true'
        aria-label={removeAriaLabel}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={handleRemoveClick}
        sx={customStyles.removeButton}
      >
        {resolvedRemoveIcon}
      </Box>
    </Box>
  );
}
